import React, { useEffect, useRef, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { fetchPostsWithPictures, loadMorePosts } from '../../redux/posts/postActions';
import Posts from '../../pages/Stockers/Posts';

const Stockers = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { 
    loading, 
    posts = [], 
    error, 
    hasMore, 
    filterLoading, 
    isFiltered 
  } = useSelector((s) => s.posts || {});

  const observer = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Infinite scroll handler
  const handleInfiniteScroll = useCallback(() => {
    // Don't load more if already loading or no more posts available
    if (loading || filterLoading || !hasMore) return;

    // Check if we're near the bottom of the page
    const scrollHeight = document.documentElement.scrollHeight;
    const scrollTop = document.documentElement.scrollTop;
    const clientHeight = document.documentElement.clientHeight;
    
    if (scrollTop + clientHeight >= scrollHeight - 100) {
      dispatch(loadMorePosts());
    }
  }, [dispatch, loading, filterLoading, hasMore]);

  // Debounced scroll handler
  const debounce = useCallback((func, wait) => {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }, []);

  // Set up scroll listener for infinite scroll
  useEffect(() => {
    const debouncedScrollHandler = debounce(handleInfiniteScroll, 300);
    
    window.addEventListener('scroll', debouncedScrollHandler);
    
    return () => {
      window.removeEventListener('scroll', debouncedScrollHandler);
    };
  }, [handleInfiniteScroll, debounce]);

  // Initialize posts on mount - but let Posts component handle this
  useEffect(() => {
    setIsInitialized(true);
  }, []);

  // Intersection Observer for bottom element (backup to scroll handler)
  useEffect(() => {
    if (!isInitialized) return;

    const handleObserver = (entries) => {
      const target = entries[0];
      if (!target.isIntersecting) return;
      if (loading || filterLoading || !hasMore) return;

      // Small delay to prevent rapid firing
      setTimeout(() => {
        dispatch(loadMorePosts());
      }, 100);
    };

    // Create intersection observer
    observer.current = new IntersectionObserver(handleObserver, { 
      threshold: 0.1,
      rootMargin: '100px'
    });

    // Find and observe the bottom element
    const bottomElement = document.querySelector('#bottom-of-posts');
    if (bottomElement && observer.current) {
      observer.current.observe(bottomElement);
    }

    return () => {
      if (observer.current) {
        observer.current.disconnect();
      }
    };
  }, [dispatch, loading, filterLoading, hasMore, isInitialized]);

  return (
    <div className="flex-1 bg-gray-900 pb-20">
      <main className="p-6 overflow-visible">
        <div className="max-w-3xl mx-auto">

          {/* Posts */}
          <Posts />

          {/* Global error display */}
          {error && !loading && !filterLoading && (
            <div className="text-center py-4 px-4 mx-4 bg-red-900/20 border border-red-500/20 rounded-lg">
              <div className="text-red-400 text-sm font-medium">
                {t('stockers.errorLoadingPosts')}
              </div>
              <div className="text-red-300 text-xs mt-1">
                {typeof error === 'object' ? JSON.stringify(error) : error}
              </div>
              <button
                onClick={() => dispatch(fetchPostsWithPictures())}
                className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs rounded-lg transition-colors"
              >
                {t('stockers.retry')}
              </button>
            </div>
          )}

          {/* Bottom marker for intersection observer */}
          <div id="bottom-of-posts" className="w-full h-1" />
        </div>
      </main>
    </div>
  );
};

export default Stockers;